import { CommonPageWrapper, HeadingWrapper } from 'admin/components/wrappers';
import AdminCustomButton from 'app/components/AdminCustomButton';
import { AdminSearch } from 'app/components/AdminSearchField';
import CustomBreadCrumb from 'app/components/CustomBreadCrumb';
import { BlurredLabel, PageMainLabel } from 'app/components/SHLabel';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useDebounce } from 'hooks/useDebounce';
import useCommonLookUpDatas from 'hooks/useCommonLookupData';
import { getLookupArray, useNavigateToRoute } from 'utils/common';
import CurrentSeasonTable from './CurrentSeasonTable';
import useShowApi from 'admin/hooks/useShowApi';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import AdminDropDown from 'app/components/AdminDropDown';
import { GeneralLookup } from 'services/openapi';
import Cookies from 'js-cookie';
import { CommonLoader } from 'app/components/CommonLoader';
import { usePermission } from 'hooks/usePermission';
import AdminMultiSelectDropDown from 'admin/components/AdminMultiSelectDropdown';
import { ShowFilteredData } from 'admin/components/AdminMultiSelectDropdown/FilteredList';

const CurrentSeasonPage = () => {
  const userType = Cookies.get('userType') ?? '';
  const { status } = useParams();
  const currentLocation = useLocation();
  const navigate = useNavigate();
  const isPastDataFlag = currentLocation.pathname.includes('past-data');
  const breadCrumbItems = [
    {
      id: '',
      text: userType === 'COACH' ? 'Showyeda coach' : 'Showyeda admin',
      disabled: true,
    },
    {
      id: '',
      text: 'Shows',
      disabled: true,
    },
  ];

  const { hasViewPermission } = usePermission();
  const {
    loading,
    fetchManageGetShows,
    showDataList,
    deleteShow,
    fetchShowActiveOrInactive,
    handleDownloadShows,
    downloadLoading,
    handleDownloadShowDetails,
  } = useShowApi();
  const { getAllLookupData, lookUps } = useCommonLookUpDatas();
  const redirect = useNavigateToRoute();

  // Initialize selectedItemId based on URL status parameter
  const getInitialSelectedItemId = () => {
    if (status === 'active') {
      return 'bc1e49f9-0f4a-4222-ba22-c4f4af0b4f75';
    } else if (status === 'inactive') {
      return 'f7f4cf58-a6fd-4646-9e25-d9517f92709e';
    }
    // No status param means show both (no filter)
    return null;
  };

  const [selectedItemId, setSelectedItemId] = useState<string | null>(getInitialSelectedItemId());
  const [active, setActive] = useState(true);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [pageSkip, setPageSkip] = useState<number | undefined>(0);
  const [pageSize, setPageSize] = useState<number>(50);
  const [pageSizeSelected, setPageSizeSelected] = useState<GeneralLookup>({
    id: '50',
    name: '50',
  });
  const [lookupData, setLookupData] = useState('');
  const [divisionIds, setDivisionIds] = useState('');
  const [categoryIds, setCategoryIds] = useState('');
  const [preDataList, setPreDataList] =
    useState<{ name: string; id: string; type: string }[]>();
  const pageSizeOptions: GeneralLookup[] = [
    { id: '25', name: '25' },
    { id: '50', name: '50' },
    { id: '100', name: '100' },
    { id: '200', name: '200' },
  ];
  const debounceShowSearch = useDebounce(searchTerm);

  // Transform filter IDs into arrays
  const divisionIdsArray = divisionIds ? divisionIds.split(',') : [];
  const categoryIdsArray = categoryIds ? categoryIds.split(',') : [];

  const handleItemIdClick = (itemId: string) => {
    const newSelectedItemId = itemId === selectedItemId ? null : itemId;
    setSelectedItemId(newSelectedItemId);

    const basePath = userType === 'COACH' ? '/coach' : '/admin';

    if (newSelectedItemId) {
      // A filter is selected
      let statusRoute = '';
      if (itemId === 'bc1e49f9-0f4a-4222-ba22-c4f4af0b4f75') {
        statusRoute = 'active';
      } else if (itemId === 'f7f4cf58-a6fd-4646-9e25-d9517f92709e') {
        statusRoute = 'inactive';
      }

      if (statusRoute) {
        const fullPath = isPastDataFlag
          ? `${basePath}/past-data/shows/${statusRoute}`
          : `${basePath}/shows/${statusRoute}`;
        navigate(fullPath, { replace: true });
      }
    } else {
      // No filter selected (deselected) - navigate to base path (shows both)
      const fullPath = isPastDataFlag
        ? `${basePath}/past-data/shows`
        : `${basePath}/shows`;
      navigate(fullPath, { replace: true });
    }
  };

  const [seasonPayload, setSeasonPayload] = useState<GeneralLookup>({
    id: '',
    name: '',
  });
  const [sortField, setSortField] = useState<string | undefined>(undefined);
  const [sortOrder, setSortOrder] = useState<number | undefined>(0);
  const [isOrder, setIsOrder] = useState(false);
  const prevPagSkipRef = useRef<number | undefined>(undefined);

  const handleSearch = e => {
    setSearchTerm(e.target.value);
  };

  const handlePageSizeChange = (data: GeneralLookup) => {
    const newPageSize = parseInt(data.id, 10);
    setPageSize(newPageSize);
    setPageSizeSelected(data);
    setPageSkip(0);
    fetchManageGetShows(
      seasonPayload?.id,
      [],
      categoryIdsArray,
      divisionIdsArray,
      selectedItemId ?? '',
      debounceShowSearch,
      sortField,
      sortOrder,
      0,
      newPageSize,
    );
  };

  const handleShowsDwoload = async () => {
    try {
      const url: any = await handleDownloadShows(
        seasonPayload?.id,
        [],
        categoryIdsArray,
        divisionIdsArray,
        selectedItemId ?? '',
        debounceShowSearch,
        sortField,
        sortOrder,
      );

      // Extract filename from URL
      const filename = url.split('/').pop();

      fetch(url)
        .then(response => response.blob())
        .then(blob => {
          const objectUrl = URL.createObjectURL(blob);

          const link = document.createElement('a');
          link.href = objectUrl;
          link.download = filename!; // Use the extracted filename

          // Append link to body (required for Firefox)
          document.body.appendChild(link);

          // Trigger download
          link.click();

          // Clean up
          document.body.removeChild(link);
          URL.revokeObjectURL(objectUrl);
        })
        .catch(error => {
          console.error('Error downloading file:', error);
        });

      return url;
    } catch (error) {
      console.error('Error downloading team point report:', error);
      throw error;
    }
  };

  const handleShowDetailsDwoload = async (showId?: string) => {
    try {
      const url: any = await handleDownloadShowDetails(undefined, showId);

      // Extract filename from URL
      const filename = url.split('/').pop();

      fetch(url)
        .then(response => response.blob())
        .then(blob => {
          const objectUrl = URL.createObjectURL(blob);

          const link = document.createElement('a');
          link.href = objectUrl;
          link.download = filename!; // Use the extracted filename

          // Append link to body (required for Firefox)
          document.body.appendChild(link);

          // Trigger download
          link.click();

          // Clean up
          document.body.removeChild(link);
          URL.revokeObjectURL(objectUrl);
        })
        .catch(error => {
          console.error('Error downloading file:', error);
        });

      return url;
    } catch (error) {
      console.error('Error downloading team point report:', error);
      throw error;
    }
  };

  const handleDropdownChange = (data: GeneralLookup) => {
    setSeasonPayload(data);
    fetchManageGetShows(
      data?.id,
      [],
      categoryIdsArray,
      divisionIdsArray,
      selectedItemId ?? '',
      debounceShowSearch,
      sortField,
      sortOrder,
      0,
      pageSize,
    );
  };

  function getPreviousSeasonConcise() {
    const seasons = getLookupArray('SEASON', lookUps);
    const adminLiveSeason = seasons.find(s => s.isAdminLive === true);

    if (!adminLiveSeason) return null;

    const yearMatch = adminLiveSeason.name.match(/Season (\d{4})-(\d{4})/);
    if (!yearMatch) return null;

    const prevYear = parseInt(yearMatch[1]) - 1;
    const prevSeasonName = `Season ${prevYear}-${yearMatch[1]}`;

    return seasons.find(s => s.name === prevSeasonName) || null;
  }

  // Effect to sync selectedItemId with URL status parameter
  useEffect(() => {
    if (status) {
      const activeStatusId =
        status === 'active'
          ? 'bc1e49f9-0f4a-4222-ba22-c4f4af0b4f75'
          : status === 'inactive'
            ? 'f7f4cf58-a6fd-4646-9e25-d9517f92709e'
            : null;
      setSelectedItemId(activeStatusId);
    }
  }, [status]);

  useEffect(() => {
    if (isPastDataFlag) {
      const previousSeasonData = getPreviousSeasonConcise();
      if (previousSeasonData) {
        setSeasonPayload(previousSeasonData);
        fetchManageGetShows(
          seasonPayload?.id === ''
            ? previousSeasonData.id
            : seasonPayload?.id,
          [],
          categoryIdsArray,
          divisionIdsArray,
          selectedItemId ?? '',
          debounceShowSearch,
          sortField,
          sortOrder,
          0,
          pageSize,
        );
      }
    } else {
      if (debounceShowSearch.length > 0) {
        fetchManageGetShows(
          seasonPayload?.id,
          [],
          categoryIdsArray,
          divisionIdsArray,
          selectedItemId ?? '',
          debounceShowSearch,
          sortField,
          sortOrder,
          0,
          pageSize,
        );
      } else if (
        (debounceShowSearch.length === 0 && selectedItemId) ||
        selectedItemId === null
      ) {
        setSeasonPayload({ id: '', name: '' });
        fetchManageGetShows(
          '',
          [],
          categoryIdsArray,
          divisionIdsArray,
          selectedItemId ?? '',
          debounceShowSearch,
          sortField,
          sortOrder,
          0,
          pageSize,
        );
      } else {
        fetchManageGetShows(
          seasonPayload?.id,
          [],
          categoryIdsArray,
          divisionIdsArray,
          selectedItemId ?? '',
          debounceShowSearch,
          sortField,
          sortOrder,
          0,
          pageSize,
        );
      }
    }

    if (pageSkip !== 0) {
      if (debounceShowSearch?.length! >= 0 || selectedItemId) {
        setPageSkip(0);
      }
    }
  }, [
    debounceShowSearch,
    selectedItemId,
    isPastDataFlag,
    ...(isPastDataFlag ? [lookUps] : []),
  ]);

  // Effect to trigger fetch when filter IDs change
  useEffect(() => {
    setPageSkip(0);
    fetchManageGetShows(
      seasonPayload?.id,
      [],
      categoryIdsArray,
      divisionIdsArray,
      selectedItemId ?? '',
      debounceShowSearch,
      sortField,
      sortOrder,
      0,
      pageSize,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [divisionIds, categoryIds]);

  useEffect(() => {
    getAllLookupData(`ACTIVESTATUS,DIVISION,TEAM,SEASON,CATEGORY`);
    Cookies.remove('showId');
  }, []);

  useEffect(() => {
    if (
      pageSkip &&
      pageSkip !== 0 &&
      showDataList?.shows?.length === 0 &&
      prevPagSkipRef.current !== pageSkip
    ) {
      const newSkip = pageSkip - pageSize;
      prevPagSkipRef.current = newSkip;

      setPageSkip(newSkip);
      fetchManageGetShows(
        seasonPayload?.id,
        [],
        categoryIdsArray,
        divisionIdsArray,
        selectedItemId ?? '',
        debounceShowSearch,
        '',
        0,
        newSkip,
        pageSize,
      );
    }
  }, [showDataList]);

  return (
    <>
      <CommonPageWrapper>
        <HeadingWrapper>
          <PageMainLabel>Shows</PageMainLabel>
          <CustomBreadCrumb preData={breadCrumbItems} />
        </HeadingWrapper>
        {downloadLoading && <CommonLoader loaderType="main" />}
        <HeadingWrapper>
          <ButtonWrapper>
            {userType !== 'COACH' && hasViewPermission('0500101002') && (
              <>
                {!isPastDataFlag && (
                  <AdminCustomButton
                    buttonType="primary"
                    isActive={false}
                    className="fill-button"
                    onClick={() => {
                      setActive(!active);
                      redirect(
                        userType === 'COACH'
                          ? `/coach/shows/create`
                          : `/admin/shows/create`,
                      );
                    }}
                  >
                    Add New Show
                  </AdminCustomButton>
                )}
              </>
            )}

            <BlurredLabel style={{ whiteSpace: 'nowrap' }}>
              Popular filter
            </BlurredLabel>
            {getLookupArray('ACTIVESTATUS', lookUps).map(item => (
              <AdminCustomButton
                key={item.id}
                buttonType="primary"
                isActive={false}
                className={
                  selectedItemId?.includes(item?.id)
                    ? 'active-inactive'
                    : 'Inactive-button'
                }
                onClick={() => {
                  handleItemIdClick(item?.id);
                }}
              >
                {item?.name}
              </AdminCustomButton>
            ))}
            <AdminMultiSelectDropDown
              data={ShowFilteredData}
              setLookupData={setLookupData}
              filteredData={getLookupArray(lookupData, lookUps)}
              lookUps={lookUps}
              lookupData={lookupData}
              setDivisionIds={setDivisionIds}
              setCategoryIds={setCategoryIds}
              setPreDataList={setPreDataList}
              preDataList={preDataList}
            />
            {isPastDataFlag && (
              <CustomDropDown
                label=""
                placeholder="Select season"
                onDataChange={data => handleDropdownChange(data)}
                defaultValue={seasonPayload}
                data={getLookupArray('SEASON', lookUps)}
                className="field-style"
              />
            )}
          </ButtonWrapper>
          <div className="search-wrapper">
            {userType !== 'COACH' && (
              <AdminCustomButton
                buttonType="primary"
                isActive={false}
                className="fill-button"
                onClick={handleShowsDwoload}
                disabled={showDataList?.shows?.length === 0}
              >
                Download
              </AdminCustomButton>
            )}

            <div style={{ width: '283px', paddingTop: '30px' }}>
              <AdminSearch
                placeholder="Search by shows name"
                onSearchTextEntering={handleSearch}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
              />
            </div>
          </div>
        </HeadingWrapper>

        {(showDataList?.pageDetails?.count ?? 0) > 10 && (
          <EntriesPerPageDropDownWrapper>
            <AdminDropDown
              label="No: of entries per page"
              onDataChange={(data: GeneralLookup) => {
                handlePageSizeChange(data);
              }}
              defaultValue={pageSizeSelected}
              data={pageSizeOptions}
            />
          </EntriesPerPageDropDownWrapper>
        )}
        {/* Shows Table */}
        {hasViewPermission('0500101001') && (
          <CurrentSeasonTable
            setPageSkip={setPageSkip}
            pageSkip={pageSkip}
            pageSize={pageSize}
            debounceShowSearch={debounceShowSearch}
            fetchManageGetShows={fetchManageGetShows}
            showData={showDataList}
            selectedItemId={selectedItemId}
            deleteShow={deleteShow}
            fetchShowActiveOrInactive={fetchShowActiveOrInactive}
            lookup={getLookupArray('ACTIVESTATUS', lookUps)}
            sortField={sortField}
            sortOrder={sortOrder}
            setSortField={setSortField}
            setSortOrder={setSortOrder}
            setIsOrder={setIsOrder}
            isOrder={isOrder}
            loading={loading}
            handleShowDetailsDwoload={handleShowDetailsDwoload}
            pageSizeSelected={pageSizeSelected}
            categoryFilter={categoryIdsArray}
            divisionFilter={divisionIdsArray}
          />
        )}
      </CommonPageWrapper>
    </>
  );
};

export default CurrentSeasonPage;
const ButtonWrapper = styled.div`
  display: flex;
  gap: 10px;
  padding: 30px 0 0;
  align-items: center;
  .field-style {
    display: flex;
  }
`;
const EntriesPerPageDropDownWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 0 0;
  .SHdropdown {
    width: 160px;

    .label {
      margin-bottom: 8px;
      font-size: 13px;
      color: #475467;
      font-weight: 500;
    }

    .k-dropdownlist {
      max-height: 38px;
      border-radius: 8px !important;
    }

    .k-input-inner {
      padding: 0 8px !important;
      font-size: 13px;
    }

    .k-input-value-text {
      margin: 4px 0;
      font-size: 13px !important;
      color: #1e1e1e;
    }
  }
`;
const CustomDropDown = styled(AdminDropDown)`
  width: 160px;
  .k-dropdownlist {
    max-height: 31px;
    border-radius: 8px !important;
  }
  .k-input-inner {
    padding: 0 5px !important;
  }
  .k-input-value-text {
    margin: 2px 4px 2px 4px;
    font-size: 12px !important;
    letter-spacing: 0px;
    color: #1e1e1e;
    text-align: left;
  }
`;
